import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from './Header';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import TextField from '@mui/material/TextField';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import Chip from '@mui/material/Chip';
import Typography from '@mui/material/Typography';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import IconButton from '@mui/material/IconButton';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import SaveIcon from '@mui/icons-material/Save';
import useProjects from '../hooks/useProjects';
import useFieldConfig from '../hooks/useFieldConfig';
import { useFirebase } from '../context/FirebaseContext';
import { getTranslations } from '../utils/translations';
import LinkTemplateBuilder from './LinkTemplateBuilder';
import './OptionsPage.css';

function CustomTabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`field-tabpanel-${index}`}
      aria-labelledby={`field-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

function a11yProps(index) {
  return {
    id: `field-tab-${index}`,
    'aria-controls': `field-tabpanel-${index}`,
  };
}

const OptionsPage = () => {
  const navigate = useNavigate();
  const { currentUser } = useFirebase();
  const { projects, loadProjects } = useProjects();
  const { loadFieldConfig, saveFieldConfig, getDefaultFieldConfig } = useFieldConfig();
  
  const [selectedProject, setSelectedProject] = useState(() => {
    return localStorage.getItem('linkcoder-selected-project') || '';
  });
  const [tabValue, setTabValue] = useState(0);
  const [fieldConfig, setFieldConfig] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  
  const [language, setLanguage] = useState(() => {
    return localStorage.getItem('linkcoder-language') || 'nl';
  });

  const fieldNames = ['Bureau', 'Medium', 'Titel', 'Doelgroep', 'Uiting', 'Linknaam', 'Campagne', 'Datum', 'Url'];

  useEffect(() => {
    if (currentUser) {
      loadProjects();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser]);

  useEffect(() => {
    if (projects.length > 0) {
      const savedProject = localStorage.getItem('linkcoder-selected-project');
      
      // Check if saved project exists in the list
      if (savedProject && projects.includes(savedProject)) {
        setSelectedProject(savedProject);
      } else if (!selectedProject) {
        // No saved project or it doesn't exist anymore, select first
        setSelectedProject(projects[0]);
        localStorage.setItem('linkcoder-selected-project', projects[0]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projects]);

  useEffect(() => {
    if (selectedProject) {
      loadConfigForProject();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProject]);

  const loadConfigForProject = async () => {
    try {
      const config = await loadFieldConfig(selectedProject);
      setFieldConfig(config);
    } catch (error) {
      console.error('Error loading field config:', error);
      setFieldConfig(getDefaultFieldConfig());
    }
  };

  const handleLanguageChange = (newLanguage) => {
    setLanguage(newLanguage);
    localStorage.setItem('linkcoder-language', newLanguage);
  };

  const handleBack = () => {
    navigate('/');
  };

  const handleProjectChange = (event) => {
    const value = event.target.value;
    setSelectedProject(value);
    localStorage.setItem('linkcoder-selected-project', value);
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleTextNormalizationChange = (property, value) => {
    setFieldConfig(prev => ({
      ...prev,
      textNormalization: {
        ...(prev.textNormalization || {}),
        [property]: value
      }
    }));
  };

  const handleFieldChange = (fieldName, property, value) => {
    setFieldConfig(prev => {
      const updatedField = {
        ...prev.fields[fieldName],
        [property]: value
      };

      // Als fieldType wordt veranderd naar 'select' en er zijn nog geen opties, voeg een lege optie toe
      if (property === 'fieldType' && value === 'select') {
        if (!updatedField.options || updatedField.options.length === 0) {
          updatedField.options = [{ Name: '', Id: '' }];
        }
      }

      return {
        ...prev,
        fields: {
          ...prev.fields,
          [fieldName]: updatedField
        }
      };
    });
  };

  const handleOptionChange = (fieldName, optionIndex, property, value) => {
    setFieldConfig(prev => {
      const newOptions = [...prev.fields[fieldName].options];
      newOptions[optionIndex] = {
        ...newOptions[optionIndex],
        [property]: value
      };
      return {
        ...prev,
        fields: {
          ...prev.fields,
          [fieldName]: {
            ...prev.fields[fieldName],
            options: newOptions
          }
        }
      };
    });
  };

  const handleAddOption = (fieldName) => {
    setFieldConfig(prev => ({
      ...prev,
      fields: {
        ...prev.fields,
        [fieldName]: {
          ...prev.fields[fieldName],
          options: [
            ...prev.fields[fieldName].options,
            { Name: '', Id: '' }
          ]
        }
      }
    }));
  };

  const handleDeleteOption = (fieldName, optionIndex) => {
    setFieldConfig(prev => {
      const newOptions = prev.fields[fieldName].options.filter((_, idx) => idx !== optionIndex);
      return {
        ...prev,
        fields: {
          ...prev.fields,
          [fieldName]: {
            ...prev.fields[fieldName],
            options: newOptions
          }
        }
      };
    });
  };

  const handleSave = async () => {
    if (!selectedProject) {
      showSnackbar(t.selectProjectFirst, 'error');
      return;
    }

    try {
      await saveFieldConfig(selectedProject, fieldConfig);
      showSnackbar(t.configSaved, 'success');
    } catch (error) {
      console.error('Error saving config:', error);
      showSnackbar(t.errorSaving, 'error');
    }
  };

  const handleResetToDefaults = () => {
    setFieldConfig(getDefaultFieldConfig());
    showSnackbar(t.configReset, 'info');
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const handleLinkTemplateChange = (newTemplate) => {
    setFieldConfig(prev => ({
      ...prev,
      linkTemplate: newTemplate
    }));
  };

  // Get translations
  const t = getTranslations(language);

  if (!currentUser || !fieldConfig) {
    return (
      <div className="wrapperDiv options-page-wrapper">
        <Header 
          language={language}
          onLanguageChange={handleLanguageChange}
        />
        <div className="options-content">
          <div className="loading-container">{t.loading}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="wrapperDiv options-page-wrapper">
      <Header 
        language={language}
        onLanguageChange={handleLanguageChange}
      />
      
      <div className="options-content">
        <div className="options-header">
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={handleBack}
            className="back-button"
          >
            {t.back}
          </Button>
          <h1 className="options-title">{t.title}</h1>
        </div>

        <Box sx={{ mb: 3, mt: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
          <FormControl sx={{ minWidth: 300 }} size="small">
            <InputLabel id="project-select-label">{t.selectProject}</InputLabel>
            <Select
              labelId="project-select-label"
              value={selectedProject}
              label={t.selectProject}
              onChange={handleProjectChange}
            >
              {projects.map((proj) => (
                <MenuItem key={proj} value={proj}>
                  {proj}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSave}
            sx={{ ml: 'auto' }}
          >
            {t.save}
          </Button>

          <Button
            variant="outlined"
            onClick={handleResetToDefaults}
          >
            {t.resetToDefaults}
          </Button>
        </Box>

        {/* Global text normalization toggles */}
        <Box sx={{ p: 2, mb: 2, border: 1, borderColor: 'divider', borderRadius: 1 }}>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>{t.textNormalization}</Typography>
          <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
            <FormControlLabel
              control={
                <Switch
                  checked={!!(fieldConfig.textNormalization?.lowercase)}
                  onChange={(e) => handleTextNormalizationChange('lowercase', e.target.checked)}
                />
              }
              label={t.lowercase}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={!!(fieldConfig.textNormalization?.replaceSpaces)}
                  onChange={(e) => handleTextNormalizationChange('replaceSpaces', e.target.checked)}
                />
              }
              label={t.replaceSpaces}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={!!(fieldConfig.textNormalization?.alphanumericOnly)}
                  onChange={(e) => handleTextNormalizationChange('alphanumericOnly', e.target.checked)}
                />
              }
              label={t.alphanumericOnly}
            />
          </Box>
        </Box>
        
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs 
            value={tabValue} 
            onChange={handleTabChange} 
            aria-label="field configuration tabs"
            variant="scrollable"
            scrollButtons="auto"
          >
            {fieldNames.map((fieldName, index) => (
              <Tab 
                key={fieldName} 
                label={fieldName} 
                {...a11yProps(index)} 
              />
            ))}
            <Tab label={t.linkTemplate} {...a11yProps(9)} />
          </Tabs>
        </Box>

        {fieldNames.map((fieldName, index) => (
          <CustomTabPanel key={fieldName} value={tabValue} index={index}>
            <Box sx={{ maxWidth: 800 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={fieldConfig.fields[fieldName].enabled}
                    onChange={(e) => handleFieldChange(fieldName, 'enabled', e.target.checked)}
                  />
                }
                label={t.fieldEnabled}
              />

              <TextField
                fullWidth
                label={t.displayName}
                value={fieldConfig.fields[fieldName].displayName}
                onChange={(e) => handleFieldChange(fieldName, 'displayName', e.target.value)}
                margin="normal"
                disabled={!fieldConfig.fields[fieldName].enabled}
              />

              <Box sx={{ mt: 2, mb: 2 }}>
                <strong>{t.fieldType}:</strong>{' '}
                {fieldConfig.fields[fieldName].fieldType === 'date' ? (
                  <Chip 
                    label={t.date}
                    size="small" 
                    color="primary" 
                    variant="outlined"
                  />
                ) : (
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <Chip 
                      label={t.text}
                      size="small" 
                      color={fieldConfig.fields[fieldName].fieldType === 'text' ? 'primary' : 'default'}
                      variant={fieldConfig.fields[fieldName].fieldType === 'text' ? 'filled' : 'outlined'}
                      onClick={() => handleFieldChange(fieldName, 'fieldType', 'text')}
                      sx={{ cursor: 'pointer' }}
                      disabled={!fieldConfig.fields[fieldName].enabled}
                    />
                    <Chip 
                      label={t.dropdown}
                      size="small" 
                      color={fieldConfig.fields[fieldName].fieldType === 'select' ? 'primary' : 'default'}
                      variant={fieldConfig.fields[fieldName].fieldType === 'select' ? 'filled' : 'outlined'}
                      onClick={() => handleFieldChange(fieldName, 'fieldType', 'select')}
                      sx={{ cursor: 'pointer' }}
                      disabled={!fieldConfig.fields[fieldName].enabled}
                    />
                  </Box>
                )}
              </Box>

              <TextField
                type="number"
                label={t.gridOrder}
                value={fieldConfig.fields[fieldName].gridOrder}
                onChange={(e) => handleFieldChange(fieldName, 'gridOrder', parseInt(e.target.value) || 0)}
                margin="normal"
                disabled={!fieldConfig.fields[fieldName].enabled}
                sx={{ width: 200, mr: 2 }}
                inputProps={{ min: 1 }}
              />

              <TextField
                type="number"
                label={t.gridWidth}
                value={fieldConfig.fields[fieldName].gridWidth}
                onChange={(e) => handleFieldChange(fieldName, 'gridWidth', parseFloat(e.target.value) || 1)}
                margin="normal"
                disabled={!fieldConfig.fields[fieldName].enabled}
                sx={{ width: 200 }}
                inputProps={{ min: 0.5, step: 0.1 }}
              />

              <Box sx={{ mt: 2 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={fieldConfig.fields[fieldName].required}
                      onChange={(e) => handleFieldChange(fieldName, 'required', e.target.checked)}
                      disabled={!fieldConfig.fields[fieldName].enabled}
                    />
                  }
                  label={t.required}
                />
              </Box>

              <Box sx={{ mt: 1 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={!!fieldConfig.fields[fieldName].autosuggest}
                      onChange={(e) => handleFieldChange(fieldName, 'autosuggest', e.target.checked)}
                      disabled={!fieldConfig.fields[fieldName].enabled || fieldConfig.fields[fieldName].fieldType !== 'text'}
                    />
                  }
                  label={t.autosuggest}
                />
                {fieldConfig.fields[fieldName].fieldType !== 'text' && (
                  <Typography variant="caption" display="block" color="text.secondary">
                    {t.autosuggestNote}
                  </Typography>
                )}
              </Box>

              {(fieldConfig.fields[fieldName].fieldType === 'select' || 
                (fieldConfig.fields[fieldName].options && fieldConfig.fields[fieldName].options.length > 0)) && 
                fieldConfig.fields[fieldName].fieldType !== 'date' && (
                <Box sx={{ mt: 3 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Box>
                      <strong>{t.dropdownOptions}</strong>
                      {fieldConfig.fields[fieldName].fieldType === 'text' && (
                        <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
                          {t.dropdownNotUsed}
                        </Typography>
                      )}
                    </Box>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<AddIcon />}
                      onClick={() => handleAddOption(fieldName)}
                      disabled={!fieldConfig.fields[fieldName].enabled || fieldConfig.fields[fieldName].fieldType === 'text'}
                    >
                      {t.addOption}
                    </Button>
                  </Box>

                  <Table 
                    size="small" 
                    sx={{ 
                      opacity: fieldConfig.fields[fieldName].fieldType === 'text' ? 0.5 : 1,
                      transition: 'opacity 0.2s ease'
                    }}
                  >
                    <TableHead>
                      <TableRow>
                        <TableCell>{t.optionDisplayName}</TableCell>
                        <TableCell>{t.optionId}</TableCell>
                        <TableCell width={60}>{t.actions}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {fieldConfig.fields[fieldName].options && fieldConfig.fields[fieldName].options.length > 0 ? (
                        fieldConfig.fields[fieldName].options.map((option, optIndex) => (
                          <TableRow key={optIndex}>
                            <TableCell>
                              <TextField
                                size="small"
                                fullWidth
                                value={option.Name || ''}
                                onChange={(e) => handleOptionChange(fieldName, optIndex, 'Name', e.target.value)}
                                disabled={!fieldConfig.fields[fieldName].enabled || fieldConfig.fields[fieldName].fieldType === 'text'}
                              />
                            </TableCell>
                            <TableCell>
                              <TextField
                                size="small"
                                fullWidth
                                value={option.Id || ''}
                                onChange={(e) => handleOptionChange(fieldName, optIndex, 'Id', e.target.value)}
                                disabled={!fieldConfig.fields[fieldName].enabled || fieldConfig.fields[fieldName].fieldType === 'text'}
                              />
                            </TableCell>
                            <TableCell>
                              <IconButton
                                size="small"
                                onClick={() => handleDeleteOption(fieldName, optIndex)}
                                disabled={!fieldConfig.fields[fieldName].enabled || fieldConfig.fields[fieldName].fieldType === 'text'}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell colSpan={3} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                            {t.noOptions}
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </Box>
              )}
            </Box>
          </CustomTabPanel>
        ))}

        <CustomTabPanel value={tabValue} index={9}>
          <LinkTemplateBuilder
            template={fieldConfig.linkTemplate}
            fields={fieldConfig.fields}
            onChange={handleLinkTemplateChange}
            language={language}
          />
        </CustomTabPanel>
      </div>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default OptionsPage;
